import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cases } from "../../../data/cases/cases";
import {
  motion,
  AnimatePresence,
  Variants,
} from "framer-motion";

const overlayItemVariants: Variants = {
  initial: { opacity: 0, y: 40 },
  animate: (custom: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.08 * custom,
      duration: 0.35,
      type: "spring",
      stiffness: 60,
    },
  }),
};

const ShowcaseSection: React.FC = () => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState<number | null>(
    null
  );
  const [isButtonHovered, setIsButtonHovered] =
    useState(false);

  const renderOverlay = (idx: number) => {
    const caseItem = cases[idx];
    if (!caseItem) return null;
    return (
      <AnimatePresence>
        {hovered === idx && (
          <div className="absolute inset-0 flex flex-col justify-end pointer-events-none z-10">
            <div className="flex flex-col gap-2 p-6">
              <motion.div
                initial="initial"
                animate="animate"
                exit="initial"
                variants={overlayItemVariants}
                custom={0}
                className="mb-2 font-bold text-lg md:text-xl"
                style={{
                  color: "#FE6019",
                  fontFamily: "Merriweather, serif",
                }}
              >
                {caseItem.name}
              </motion.div>
              <motion.div
                initial="initial"
                animate="animate"
                exit="initial"
                variants={overlayItemVariants}
                custom={1}
                className={`mb-4 text-xs md:text-sm text-white dark:text-gray-200 ${
                  idx === 0
                    ? "line-clamp-2" // 2 lines for left grid
                    : "line-clamp-4" // 4 lines for right grid
                }`}
                style={{
                  fontFamily: "Poppins, sans-serif",
                  textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                }}
              >
                {caseItem.description}
              </motion.div>
              <motion.button
                initial="initial"
                animate="animate"
                exit="initial"
                variants={overlayItemVariants}
                custom={2}
                className="flex items-center self-start px-4 py-2 rounded-md border border-[#FE6019] text-[#FE6019] font-semibold text-xs md:text-sm bg-black/60 hover:bg-[#FE6019] hover:text-white transition-colors duration-200"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  pointerEvents: "auto",
                }}
                onClick={() =>
                  navigate(
                    `/cases/${caseItem.name
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`
                  )
                }
              >
                See more
                <svg
                  className="ml-2 w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 12h14M12 5l7 7-7 7"
                  />
                </svg>
              </motion.button>
            </div>
          </div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <section className="w-full flex flex-col items-center py-16 md:py-24 bg-white dark:bg-black transition-colors duration-500">
      <div className="max-w-5xl w-full mx-auto flex flex-col items-center px-4">
        <div className="w-full flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
          <div className="flex flex-col items-start">
            <span className="inline-block mb-3 px-4 py-1 rounded-full border border-[#FE6019] text-[#FE6019] bg-[#FE6019]/10 font-semibold text-xs md:text-sm tracking-wide">
              CASES
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-0 mt-0">
              Our notable accomplishments
            </h2>
          </div>
          <button
            className={`relative flex items-center overflow-hidden font-semibold text-base sm:text-lg shadow-md transition-all duration-300 rounded-lg px-6 py-3 mt-2
              ${
                isButtonHovered
                  ? "bg-black dark:bg-white text-white dark:text-black"
                  : "bg-orange-500 dark:bg-orange-600 text-white"
              }
            `}
            onClick={() => navigate("/cases")}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
          >
            <span className="z-10 transition-colors duration-300">
              DISCOVER NOW
            </span>
            <span
              className={`flex items-center transition-all duration-300 ml-2 ${
                isButtonHovered
                  ? "translate-x-0 opacity-100"
                  : "translate-x-6 opacity-0"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 12h14M12 5l7 7-7 7"
                />
              </svg>
            </span>
          </button>
        </div>
        <div className="w-full flex flex-col md:flex-row gap-6">
          {/* Left big image - First case */}
          <div
            className="flex-1 min-w-[220px] aspect-square md:aspect-auto md:h-[500px] rounded-xl overflow-hidden relative cursor-pointer group"
            onMouseEnter={() => setHovered(0)}
            onMouseLeave={() => setHovered(null)}
            onClick={() =>
              navigate(
                `/cases/${cases[0].name
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`
              )
            }
          >
            <img
              src={cases[0].image}
              alt={cases[0].name}
              className={`w-full h-full object-cover transition-transform duration-500 ${
                hovered === 0 ? "scale-110" : "scale-100"
              }`}
              style={{ minHeight: 220 }}
            />
            <div
              className={`absolute inset-0 bg-black transition-all duration-500 ${
                hovered === 0
                  ? "bg-opacity-40"
                  : "bg-opacity-0"
              }`}
            ></div>
            {renderOverlay(0)}
          </div>
          {/* Right grid: top = case 2, bottom left = case 3, bottom right = case 4 */}
          <div className="flex flex-col gap-6 flex-1 min-w-[220px]">
            <div className="flex w-full h-1/2">
              <div
                className="flex-1 aspect-[2/1] rounded-xl overflow-hidden relative cursor-pointer group"
                onMouseEnter={() => setHovered(1)}
                onMouseLeave={() => setHovered(null)}
                onClick={() =>
                  navigate(
                    `/cases/${cases[1].name
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`
                  )
                }
              >
                <img
                  src={cases[1].image}
                  alt={cases[1].name}
                  className={`w-full h-full object-cover transition-transform duration-500 ${
                    hovered === 1
                      ? "scale-110"
                      : "scale-100"
                  }`}
                />
                <div
                  className={`absolute inset-0 bg-black transition-all duration-500 ${
                    hovered === 1
                      ? "bg-opacity-40"
                      : "bg-opacity-0"
                  }`}
                ></div>
                {renderOverlay(1)}
              </div>
            </div>
            <div className="flex gap-6 w-full h-1/2">
              <div
                className="flex-1 aspect-square rounded-xl overflow-hidden relative cursor-pointer group"
                onMouseEnter={() => setHovered(2)}
                onMouseLeave={() => setHovered(null)}
                onClick={() =>
                  navigate(
                    `/cases/${cases[2].name
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`
                  )
                }
              >
                <img
                  src={cases[2].image}
                  alt={cases[2].name}
                  className={`w-full h-full object-cover transition-transform duration-500 ${
                    hovered === 2
                      ? "scale-110"
                      : "scale-100"
                  }`}
                />
                <div
                  className={`absolute inset-0 bg-black transition-all duration-500 ${
                    hovered === 2
                      ? "bg-opacity-40"
                      : "bg-opacity-0"
                  }`}
                ></div>
                {renderOverlay(2)}
              </div>
              <div
                className="flex-1 aspect-square rounded-xl overflow-hidden relative cursor-pointer group"
                onMouseEnter={() => setHovered(3)}
                onMouseLeave={() => setHovered(null)}
                onClick={() =>
                  navigate(
                    `/cases/${cases[3].name
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`
                  )
                }
              >
                <img
                  src={cases[3].image}
                  alt={cases[3].name}
                  className={`w-full h-full object-cover transition-transform duration-500 ${
                    hovered === 3
                      ? "scale-110"
                      : "scale-100"
                  }`}
                />
                <div
                  className={`absolute inset-0 bg-black transition-all duration-500 ${
                    hovered === 3
                      ? "bg-opacity-40"
                      : "bg-opacity-0"
                  }`}
                ></div>
                {renderOverlay(3)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShowcaseSection;
